import React, { useEffect, useState } from 'react';
import { Facebook, ArrowRight, Check } from 'lucide-react';
import { fetchCMSContent } from '../lib/cms';
import { motion } from 'framer-motion';

const PriceList = () => {
  const [materials, setMaterials] = useState([]);

  useEffect(() => {
    const loadMaterials = async () => {
      const data = await fetchCMSContent('materials');
      setMaterials(data);
    };
    loadMaterials();
  }, []);

  const flisMaterials = materials.filter(item => item.category === 'Flis');
  const bulkMaterials = materials.filter(item => !item.price_per_unit && item.category !== 'Flis');
  const unitMaterials = materials.filter(item => item.price_per_unit);

  return (
    <section id="priser" className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-black mb-4">Tydelige standardpriser</h2>
          <p className="text-xl text-zinc-600 max-w-2xl mx-auto">
            Vi gør det nemt at gennemskue prisen. Alle priser er inkl. moms og gælder ved afhentning.
          </p>
        </motion.div>

        <div className="space-y-12">
          {/* Bulk Materials Section */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative bg-white rounded-none shadow-2xl border border-zinc-100 overflow-hidden"
          >
            <div className="bg-zinc-50 px-6 py-4 border-b border-zinc-100">
              <h3 className="text-lg font-black uppercase tracking-wider text-black">Afhentning i løs vægt (Murerbalje / Trailer)</h3>
            </div>

            {/* Desktop Table View (Hidden on Mobile) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-black text-white">
                    <th className="px-6 lg:px-8 py-6 text-sm font-black uppercase tracking-wider w-1/3">Materiale</th>
                    <th className="px-4 py-6 text-sm font-black uppercase tracking-wider text-right">Murerbalje <span className="block text-[10px] font-normal text-zinc-400">90L</span></th>
                    <th className="px-4 py-6 text-sm font-black uppercase tracking-wider text-right">Lille trailer <span className="block text-[10px] font-normal text-zinc-400">300 - 750kg</span></th>
                    <th className="px-6 lg:px-8 py-6 text-sm font-black uppercase tracking-wider text-right">Stor trailer <span className="block text-[10px] font-normal text-zinc-400">751 - 1.300kg</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {bulkMaterials.map((item, index) => (
                    <motion.tr 
                      key={item.id}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="hover:bg-zinc-50/50 transition-colors"
                    >
                      <td className="px-6 lg:px-8 py-6">
                        <div className="flex items-start gap-3">
                          <Check className="text-black mt-1 shrink-0" size={18} />
                          <div>
                            <span className="font-bold text-zinc-900 text-lg block leading-tight">{item.name.da}</span>
                            <span className="text-zinc-500 text-xs font-medium uppercase tracking-wider mt-1 block">
                              {item.category}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-6 text-right whitespace-nowrap">
                        <div className="flex flex-col items-end">
                          <span className="text-xl font-black text-black leading-none">{item.price_murerbalje},-</span>
                          <span className="text-zinc-500 text-[10px] mt-1 uppercase font-bold">kr.</span>
                        </div>
                      </td>
                      <td className="px-4 py-6 text-right whitespace-nowrap">
                        <div className="flex flex-col items-end">
                          <span className="text-xl font-black text-black leading-none">{item.price_trailer_small},-</span>
                          <span className="text-zinc-500 text-[10px] mt-1 uppercase font-bold">kr.</span>
                        </div>
                      </td>
                      <td className="px-6 lg:px-8 py-6 text-right whitespace-nowrap">
                        <div className="flex flex-col items-end">
                          <span className="text-xl font-black text-black leading-none">{item.price_trailer_large},-</span>
                          <span className="text-zinc-500 text-[10px] mt-1 uppercase font-bold">kr.</span>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Stacked View (Hidden on Desktop) */}
            <div className="block md:hidden divide-y divide-zinc-100">
              {bulkMaterials.map((item, index) => (
                <div 
                  key={item.id}
                  className="p-5 bg-white"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-bold text-zinc-900 text-lg leading-tight">{item.name.da}</h3>
                      <span className="inline-block bg-zinc-100 text-zinc-600 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 mt-1">
                        {item.category}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-zinc-50 p-2.5">
                      <span className="block text-[10px] text-zinc-500 font-bold uppercase mb-1">Murerbalje</span>
                      <span className="block text-sm font-black text-black">{item.price_murerbalje},-</span>
                      <span className="block text-[9px] text-zinc-400">90L</span>
                    </div>
                    <div className="bg-zinc-50 p-2.5">
                      <span className="block text-[10px] text-zinc-500 font-bold uppercase mb-1">Lille trailer</span>
                      <span className="block text-sm font-black text-black">{item.price_trailer_small},-</span>
                      <span className="block text-[9px] text-zinc-400">300-750kg</span>
                    </div>
                    <div className="bg-zinc-50 p-2.5">
                      <span className="block text-[10px] text-zinc-500 font-bold uppercase mb-1">Stor trailer</span>
                      <span className="block text-sm font-black text-black">{item.price_trailer_large},-</span>
                      <span className="block text-[9px] text-zinc-400">751-1300kg</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Flis / Akselmængde Section */}
          {flisMaterials.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative bg-white rounded-none shadow-2xl border border-zinc-100 overflow-hidden"
            >
              <div className="bg-zinc-50 px-6 py-4 border-b border-zinc-100">
                <h3 className="text-lg font-black uppercase tracking-wider text-black">Afhentning af Flis (Priser efter akselmængde)</h3>
              </div>

              {/* Desktop Table View (Hidden on Mobile) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-black text-white">
                      <th className="px-6 lg:px-8 py-6 text-sm font-black uppercase tracking-wider w-1/3">Materiale</th>
                      <th className="px-4 py-6 text-sm font-black uppercase tracking-wider text-right">Murerbalje <span className="block text-[10px] font-normal text-zinc-400">90L</span></th>
                      <th className="px-4 py-6 text-sm font-black uppercase tracking-wider text-right">Trailer <span className="block text-[10px] font-normal text-zinc-400">1 aksel</span></th>
                      <th className="px-6 lg:px-8 py-6 text-sm font-black uppercase tracking-wider text-right">Trailer <span className="block text-[10px] font-normal text-zinc-400">2 aksler</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {flisMaterials.map((item, index) => (
                      <motion.tr 
                        key={item.id}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="hover:bg-zinc-50/50 transition-colors"
                      >
                        <td className="px-6 lg:px-8 py-6">
                          <div className="flex items-start gap-3">
                            <Check className="text-black mt-1 shrink-0" size={18} />
                            <div>
                              <span className="font-bold text-zinc-900 text-lg block leading-tight">{item.name.da}</span>
                              <span className="text-zinc-500 text-xs font-medium uppercase tracking-wider mt-1 block">
                                {item.category}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-6 text-right whitespace-nowrap">
                          <div className="flex flex-col items-end">
                            <span className="text-xl font-black text-black leading-none">{item.price_murerbalje},-</span>
                            <span className="text-zinc-500 text-[10px] mt-1 uppercase font-bold">kr.</span>
                          </div>
                        </td>
                        <td className="px-4 py-6 text-right whitespace-nowrap">
                          <div className="flex flex-col items-end">
                            <span className="text-xl font-black text-black leading-none">{item.price_trailer_small},-</span>
                            <span className="text-zinc-500 text-[10px] mt-1 uppercase font-bold">kr.</span>
                          </div>
                        </td>
                        <td className="px-6 lg:px-8 py-6 text-right whitespace-nowrap">
                          <div className="flex flex-col items-end">
                            <span className="text-xl font-black text-black leading-none">{item.price_trailer_large},-</span>
                            <span className="text-zinc-500 text-[10px] mt-1 uppercase font-bold">kr.</span>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Stacked View (Hidden on Desktop) */}
              <div className="block md:hidden divide-y divide-zinc-100">
                {flisMaterials.map((item, index) => (
                  <div 
                    key={item.id}
                    className="p-5 bg-white"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-bold text-zinc-900 text-lg leading-tight">{item.name.da}</h3>
                        <span className="inline-block bg-zinc-100 text-zinc-600 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 mt-1">
                          {item.category}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-zinc-50 p-2.5">
                        <span className="block text-[10px] text-zinc-500 font-bold uppercase mb-1">Murerbalje</span>
                        <span className="block text-sm font-black text-black">{item.price_murerbalje},-</span>
                        <span className="block text-[9px] text-zinc-400">90L</span>
                      </div>
                      <div className="bg-zinc-50 p-2.5">
                        <span className="block text-[10px] text-zinc-500 font-bold uppercase mb-1">1 aksel</span>
                        <span className="block text-sm font-black text-black">{item.price_trailer_small},-</span>
                        <span className="block text-[9px] text-zinc-400">Trailer</span>
                      </div>
                      <div className="bg-zinc-50 p-2.5">
                        <span className="block text-[10px] text-zinc-500 font-bold uppercase mb-1">2 aksler</span>
                        <span className="block text-sm font-black text-black">{item.price_trailer_large},-</span>
                        <span className="block text-[9px] text-zinc-400">Trailer</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Unit Materials Section (e.g., Mark sten) */}
          {unitMaterials.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white border border-zinc-100 shadow-xl overflow-hidden"
            >
              <div className="bg-zinc-50 px-6 py-4 border-b border-zinc-100">
                <h3 className="text-lg font-black uppercase tracking-wider text-black">Materialer pr. styk</h3>
              </div>
              <div className="p-6 md:p-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {unitMaterials.map((item) => (
                  <div key={item.id} className="border border-zinc-200 p-6 flex flex-col justify-between bg-zinc-50/20 hover:border-black transition-colors">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Check className="text-black shrink-0" size={18} />
                        <h4 className="font-bold text-zinc-950 text-xl leading-tight">{item.name.da}</h4>
                      </div>
                      <span className="text-zinc-500 text-xs font-medium uppercase tracking-wider block mb-4">
                        {item.category}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between pt-4 border-t border-zinc-100">
                      <span className="text-zinc-600 text-sm font-bold uppercase tracking-wider">Stykpris</span>
                      <div className="text-right">
                        <span className="text-3xl font-black text-black">{item.price_per_unit},- kr.</span>
                        <span className="block text-[10px] text-zinc-500 font-bold uppercase mt-1">Sælges kun stykvis</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white p-6 sm:p-8 border border-zinc-100 shadow-lg"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h4 className="text-xl font-bold text-black mb-2">Se aktuelle kampagner på Facebook</h4>
                <p className="text-zinc-600">Vi kører løbende tilbud på udvalgte materialer. Følg os for at se de nyeste priser.</p>
              </div>
              <a
                href="https://www.facebook.com/paapladsen"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-black text-white px-8 py-4 rounded-none font-bold hover:bg-zinc-800 transition-all shadow-lg group w-full md:w-auto justify-center"
              >
                <i className="fa-brands fa-facebook text-lg" />
                Gå til Facebook
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PriceList;
